import { useViewMode } from '@/shared/hooks/useViewMode';
import { Avatar, Chip, StatusDot } from '@/shared/ui/atoms';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { SortPicker } from '@/features/project-board/components/SortPicker';
import { DateSort, type SortKey } from '@/features/project-board/sorting';
import { useBoard } from '@/features/project-board/BoardProvider';
import type { DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import { ISSUE_STAGE_COLUMNS, type Issue } from '@/features/project-board/types';
import { ViewFrame } from '@/features/project-board/views/ViewFrame';
import { ItemKey } from '@/features/project-board/identity';
import { OwnerRoster } from '@/features/project-board/owners';

/**
 * 처리자 표시 — 담당자 필터와 같은 판정(`memberOf`)을 거친 이름을 우선 쓴다.
 *
 * 필터는 명부 기준 이름으로 좁히는데 화면에 커밋 author 원문(대소문자·표기 차이)을 그대로 실으면,
 * 담당자를 골라도 화면에 다른 표기가 보여 안 걸러진 것처럼 보인다. 명부에 없는 값(AI 에이전트 등)은
 * 원문을 그대로 보여준다 — 필터로는 못 고르지만 화면에서는 누가 손댔는지 알아야 한다.
 */
function handlerOf(issue: Pick<Issue, 'handledBy'>): string {
  return OwnerRoster.memberOf(issue.handledBy) || issue.handledBy;
}

const EDGE: Record<string, string> = {
  todo: 'border-l-todo',
  progress: 'border-l-progress',
  done: 'border-l-done',
};

const STATUS_LABEL: Record<string, string> = {
  todo: '대기',
  progress: '진행 중',
  done: '완료',
};

type IssueViewMode = 'stage' | 'table';

const VIEW_MODES: ReadonlyArray<ViewModeOption<IssueViewMode>> = [
  { key: 'stage', label: '단계', glyph: '▤' },
  { key: 'table', label: '테이블', glyph: '☰' },
];

const SORT_OPTIONS = DateSort.options('제보일');

export function IssueView(): React.JSX.Element {
  const { issues: scoped } = useBoard();
  const [mode, setMode] = useViewMode<IssueViewMode>('project-board.issue.view', 'stage');
  // 기본 정렬은 최근 등록일(제보일) 순.
  const [sort, setSort] = useViewMode<SortKey>('project-board.issue.sort', 'date-desc');

  // 이슈의 등록 시점은 제보 시각이다. 같은 날은 id로 순서를 고정한다.
  const issues = DateSort.sort(scoped, sort, (issue) => issue.reportedAt, (issue) => issue.id);

  return (
    <ViewFrame
      scope="issues"
      countLabel={`${issues.length}개 이슈`}
      isEmpty={issues.length === 0}
      emptyMessage="조건에 맞는 이슈가 없다."
      toolbar={
        <div className="flex items-center gap-2">
          <SortPicker options={SORT_OPTIONS} value={sort} onChange={setSort} label="이슈 정렬" />
          <ViewModeToggle options={VIEW_MODES} value={mode} onChange={setMode} />
        </div>
      }
      render={(open) =>
        mode === 'table' ? <IssueTable issues={issues} open={open} /> : <StageBoard issues={issues} open={open} />
      }
    />
  );
}

/** 이슈는 상태가 아니라 **하네스 단계**가 컬럼이다 — 접수→조사→계획→수정→리뷰→E2E. */
function StageBoard({
  issues,
  open,
}: {
  issues: Issue[];
  open: (payload: DrawerPayload) => void;
}): React.JSX.Element {
  return (
    <div className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] items-start gap-3 overflow-x-auto pb-2">
      {ISSUE_STAGE_COLUMNS.map((column) => {
        const items = issues.filter((issue) => issue.stage === column.key);
        return (
          <div key={column.key} className="min-w-[280px] rounded-lg border border-line-soft bg-[#f0f3f6]">
            <div className="flex items-center gap-1.5 border-b border-line-soft px-3 py-2.5 text-xs font-semibold text-muted">
              {column.label}
              <span className="ml-auto rounded-full bg-surface px-1.5 text-[11px]">{items.length}</span>
            </div>
            <div className="flex max-h-[calc(100vh-230px)] flex-col gap-2 overflow-y-auto p-2.5">
              {items.map((issue) => (
                <article
                  key={ItemKey.issue(issue)}
                  title={issue.title}
                  onClick={() => open(DrawerPayloads.issue(issue))}
                  className={`cursor-pointer rounded-md border border-line border-l-[3px] bg-surface p-2.5 hover:border-accent ${EDGE[issue.status]}`}
                >
                  <div className="line-clamp-3 text-[13px] leading-snug">{issue.titleHead}</div>
                  {issue.titleDetail && (
                    <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted">{issue.titleDetail}</div>
                  )}
                  <footer className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-[11px] text-muted">{issue.reportedAt || issue.id}</span>
                    {issue.repos.slice(0, 2).map((repo) => (
                      <Chip tone="repo" key={repo}>
                        {repo}
                      </Chip>
                    ))}
                    {issue.skippedStages.length > 0 && (
                      <Chip tone="warn">⚠ {issue.skippedStages.join(',')} 없음</Chip>
                    )}
                    <span className="flex-1" />
                    {issue.handledBy && <Avatar name={handlerOf(issue)} />}
                  </footer>
                </article>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 테이블 뷰 — 단계 보드가 "어디까지 갔나"를 보는 화면이라면 이건 **줄 세워 비교하는 화면**이다.
 * 카드에서 잘리던 제목·repo를 다 보여주고, 단계 보드에서는 컬럼 위치로만 알던 단계를 값으로 세운다.
 */
function IssueTable({
  issues,
  open,
}: {
  issues: Issue[];
  open: (payload: DrawerPayload) => void;
}): React.JSX.Element {
  return (
    // 이 래퍼에 overflow를 주면 thead sticky 기준이 래퍼로 바뀌어 헤더가 첫 행 뒤로 밀린다 — 주지 않는다.
    <div className="rounded-lg border border-line bg-surface">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[100px]" />
          <col />
          <col className="w-[132px]" />
          <col className="w-[88px]" />
          <col className="w-[170px]" />
          <col className="w-[128px]" />
          <col className="w-[96px]" />
        </colgroup>
        <thead>
          <tr className="[&>th]:sticky [&>th]:top-topbar [&>th]:z-10 [&>th]:bg-[#f0f3f6] [&>th]:px-2.5 [&>th]:py-2.5 [&>th]:text-left [&>th]:align-middle [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted [&>th]:shadow-[inset_0_-1px_0_var(--color-line)]">
            <th>제보일</th>
            <th>이슈</th>
            <th>단계</th>
            <th>상태</th>
            <th>Repo</th>
            <th>처리자</th>
            <th>갱신</th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue) => (
            <tr
              key={ItemKey.issue(issue)}
              onClick={() => open(DrawerPayloads.issue(issue))}
              className="cursor-pointer border-b border-line-soft last:border-0 hover:bg-canvas [&>td]:px-2.5 [&>td]:py-2.5 [&>td]:align-top"
            >
              <td className="font-mono text-[11.5px] whitespace-nowrap text-muted">{issue.reportedAt || '-'}</td>
              <td>
                <div className="text-[13.5px] leading-snug font-semibold">{issue.titleHead}</div>
                {issue.titleDetail && (
                  <div className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-muted">{issue.titleDetail}</div>
                )}
                <div className="mt-0.5 truncate font-mono text-[11px] text-faint">{issue.slug}</div>
              </td>
              <td>
                <div className="text-[12px] font-semibold">{issue.stageLabel}</div>
                {issue.skippedStages.length > 0 && (
                  <div className="mt-0.5 text-[11px] text-amber-800" title={issue.skippedStages.join(', ')}>
                    ⚠ {issue.skippedStages.length}단계 건너뜀
                  </div>
                )}
              </td>
              <td className="text-[12px] whitespace-nowrap">
                <StatusDot status={issue.status} />
                {STATUS_LABEL[issue.status] ?? issue.status}
              </td>
              <td>
                <div className="flex flex-wrap gap-1">
                  {issue.repos.length > 0 ? (
                    issue.repos.map((repo) => (
                      <Chip tone="repo" key={repo}>
                        {repo}
                      </Chip>
                    ))
                  ) : (
                    <span className="text-[11px] text-faint">-</span>
                  )}
                </div>
              </td>
              <td>
                {issue.handledBy ? (
                  <span className="flex items-center gap-1.5 text-[11.5px] text-muted">
                    <Avatar name={handlerOf(issue)} />
                    <span className="truncate">{handlerOf(issue)}</span>
                  </span>
                ) : (
                  <span className="text-[11px] text-faint">-</span>
                )}
              </td>
              <td className="text-[11px] whitespace-nowrap text-muted">{issue.updatedAt ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
